<script>
import useVuelidate from '@vuelidate/core';
import {required, requiredIf} from '@vuelidate/validators';
import {useSidebarStore} from '@store/sidebarStore';

/* COMPOSANTS */
import AkLoader from '@components/general/AkLoader.vue';
import GaMessages from '@components/ga/layout/GaMessages.vue';
import AkFormSubmitted from '@components/general/AkFormSubmitted';
import AkInputMoney from '@components/v2/input/AkInputMoney';
import AkCalendar from '@components/v2/input/AkCalendar';
import AkButtonFooterSidebar from '@components/v2/general/AkButtonFooterSidebar.vue';

/* MIXINS */
import sidebarMixin from '@mixins/sidebarMixin';
import utilsMixin from '@mixins/utilsMixin';
import messageMixin from '@mixins/messageMixin';

/* SERVICES */
import reservationService from '@services/reservationService';

export default {
  components: {AkLoader, GaMessages, AkFormSubmitted, AkInputMoney, AkCalendar, AkButtonFooterSidebar},
  mixins: [sidebarMixin, utilsMixin, messageMixin],
  props: {
    modelValue: {required: true},
    callback: {
      type: Array,
      required: false,
      default: undefined,
    },
  },
  setup() {
    return {v$: useVuelidate()};
  },
  data() {
    return {
      reservation: undefined,
      itemLitige: {},
      submitted: false,
    };
  },
  validations() {
    return {
      itemLitige: {
        dateLitige: {required},
        amountLitigeEstimated: {required},
        amountLitigeReported: {required: requiredIf(this.itemLitige.dateStartLitige)},
        amountLitigeOwner: {required: requiredIf(this.itemLitige.dateStartLitige)},
        amountLitigeGav: {required: requiredIf(this.itemLitige.dateStartLitige)},
        amountLitigeGas: {required: requiredIf(this.itemLitige.dateStartLitige)},
      },
    };
  },
  mounted() {
    this.$refs.loader.load();

    reservationService
      .reservationLitige(this.currentValue)
      .then((data) => {
        this.reservation = data;

        this.itemLitige = {...this.reservation};
        this.itemLitige.estimated = !!this.itemLitige.amountLitigeEstimated;

        if (this.itemLitige.dateStartLitige) {
          this.itemLitige.amountLitigeGas = 0;
          this.itemLitige.amountLitigeGav = 0;
          this.itemLitige.amountLitigeOwner = 0;
        }
      })
      .finally(() => this.$refs.loader.hide());
  },
  computed: {
    currentValue: {
      get: function () {
        return this.modelValue;
      },
      set: function (val) {
        this.$emit('update:modelValue', val);
      },
    },
    dateCreated() {
      if (!this.reservation) return '';
      let date = this.reservation.dateCreated;
      return date ? this.displayDate(date) : '-';
    },
  },
  methods: {
    close() {
      useSidebarStore().$reset();
    },

    create() {
      this.submitted = true;
      this.v$.itemLitige.$touch();

      if (this.v$.itemLitige.$error) {
        this.$refs.submit.init();
        return;
      }

      if (this.itemLitige.dateStartLitige) {
        let total = this.itemLitige.amountLitigeReported;
        let totalGas = this.itemLitige.amountLitigeGas;
        let totalGav = this.itemLitige.amountLitigeGav;
        let totalOwner = this.itemLitige.amountLitigeOwner;

        if (total) {
          if (this.addNumbers([totalGas, totalGav, totalOwner]) !== total) {
            this.$refs.submit.init();
            this.$refs.sidebarMessages.error("Le total des parts n'est pas égal au montant encaissé");
            this.$refs.submit.init();
            return;
          }
        }
        this.itemLitige.dateEndLitige = new Date(this.itemLitige.dateLitige);
      } else this.itemLitige.dateStartLitige = new Date(this.itemLitige.dateLitige);

      this.itemLitige.dateLitige = null;
      this.$refs.loader.load();

      reservationService
        .updateLitige(this.itemLitige)
        .then((data) => {
          this.callback[0](true, this.itemLitige.dateEndLitige);
        })
        .catch(() => {
          this.callback[0](false, this.itemLitige.dateEndLitige);
        })
        .finally(() => {
          this.submitted = false;
          this.close();
        });
    },
  },
};
</script>

<template>
  <AkLoader ref="loader" :scale="2" color="var(--color-primary)" message="Récupération en cours">
    <template #content>
      <perfect-scrollbar class="h-full flex flex-col gap-4 md:gap-2" :options="{suppressScrollX: true}">
        <div>
          <GaMessages ref="sidebarMessages" />
        </div>
        <div class="flex flex-col gap-4 mb-4">
          <div class="flex justify-between">
            <div>
              <div class="font-inter text-grey-900 font-medium text-md">
                Réservation
                <router-link
                  @click="close"
                  class="font-inter text-grey-900 font-medium text-md"
                  :to="{
                    name: 'reservationPage',
                    params: {reservationId: reservation.id},
                  }"
                  >{{ reservation.localizer }}
                </router-link>
              </div>
              <div class="font-inter text-grey-400 text-sm font-medium">le {{ this.dateCreated }}</div>
            </div>
          </div>
        </div>
        <AkFormSubmitted :submitted="submitted" class="flex flex-col md:gap-4 gap-2" reference="form">
          <div class="flex flex-col gap-4">
            <AkCalendar
              v-if="itemLitige.dateStartLitige"
              v-model="itemLitige.dateStartLitige"
              :maxDate="new Date()"
              :label="$t('reservation.dateStartLitige')"
              :disabled="true" />

            <AkCalendar
              v-model="itemLitige.dateLitige"
              :maxDate="new Date()"
              :validator="v$.itemLitige.dateLitige"
              :label="$t(itemLitige.dateStartLitige ? 'reservation.dateEndLitige' : 'reservation.dateStartLitige')"
              :min-date="itemLitige.dateStartLitige ? itemLitige.dateStartLitige : null" />

            <AkInputMoney
              v-model="itemLitige.amountLitigeEstimated"
              :label="$t('reservation.amountLitigeEstimated')"
              :disabled="this.itemLitige.estimated"
              :validator="v$.itemLitige.amountLitigeEstimated" />

            <AkInputMoney
              v-if="itemLitige.dateStartLitige"
              v-model="itemLitige.amountLitigeReported"
              :label="$t('reservation.amountLitigeReported')"
              :validator="v$.itemLitige.amountLitigeReported"
              :required="true" />
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4" v-if="itemLitige.dateStartLitige">
              <AkInputMoney
                v-model="itemLitige.amountLitigeGas"
                :label="$t('reservation.amountLitigeGas')"
                :validator="v$.itemLitige.amountLitigeGas" />
              <AkInputMoney
                v-model="itemLitige.amountLitigeGav"
                :label="$t('reservation.amountLitigeGav')"
                :validator="v$.itemLitige.amountLitigeGav" />
              <AkInputMoney
                v-model="itemLitige.amountLitigeOwner"
                :label="$t('reservation.amountLitigeOwner')"
                :validator="v$.itemLitige.amountLitigeOwner" />
            </div>
          </div>
          <AkButtonFooterSidebar
            ref="submit"
            :cancelLabel="$t('cancel')"
            :validateLabel="$t('create')"
            @action="create"
            @cancel="close" />
        </AkFormSubmitted>
      </perfect-scrollbar>
    </template>
  </AkLoader>
</template>
